import { useState, useEffect, useRef, useCallback } from 'react'
import { chatService, type TargetUsernameAutocompleteResult } from '@/services'
import { cn } from '@/lib/utils'

interface MentionAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  rows?: number
  onKeyPress?: (e: React.KeyboardEvent) => void
  onMentionAdd?: (mention: TargetUsernameAutocompleteResult) => void
  onMentionRemove?: (mentionId: string) => void
}

interface MentionData {
  position: number
  query: string
  isActive: boolean
}

export function MentionAutocomplete({
  value,
  onChange,
  placeholder = "Type your message...",
  className,
  disabled = false,
  rows = 3,
  onKeyPress,
  onMentionAdd,
  onMentionRemove
}: MentionAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<TargetUsernameAutocompleteResult[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mentionData, setMentionData] = useState<MentionData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Track selected mentions by their target_username for removal detection
  const selectedMentionsRef = useRef<Map<string, TargetUsernameAutocompleteResult>>(new Map())

  // Parse input to detect @mentions
  const parseMention = useCallback((text: string, cursorPosition: number): MentionData | null => {
    // Find the last @ before cursor position
    const beforeCursor = text.substring(0, cursorPosition)
    const lastAtIndex = beforeCursor.lastIndexOf('@')
    
    if (lastAtIndex === -1) return null
    
    // Get text after the @ until cursor or next space/newline
    const afterAt = text.substring(lastAtIndex + 1, cursorPosition)
    
    // Check if there's a space or newline in the mention text (invalid)
    if (afterAt.includes(' ') || afterAt.includes('\n')) {
      return null
    }
    
    const mentionData = {
      position: lastAtIndex,
      query: afterAt,
      isActive: true
    }
    
    return mentionData
  }, [])

  // Fetch suggestions when mention is detected
  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length === 0) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    try {
      const results = await chatService.autocompleteTargetUsername({
        prefix: query,
        limit: 10,
        search_instagram_username: true
      })
      setSuggestions(results)
      setSelectedIndex(0)
    } catch (error) {
      console.error('❌ Failed to fetch autocomplete suggestions:', error)
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Handle text change and detect mentions
  const handleTextChange = useCallback((newValue: string) => {
    onChange(newValue)
    
    // Check for removed mentions
    if (onMentionRemove) {
      const currentMentions = selectedMentionsRef.current
      currentMentions.forEach((mention, username) => {
        if (!newValue.includes(`@${username}`)) {
          currentMentions.delete(username)
          onMentionRemove(mention.id)
        }
      })
    }
    
    const textarea = textareaRef.current
    if (!textarea) return

    const cursorPosition = textarea.selectionStart || 0
    
    const mention = parseMention(newValue, cursorPosition)
    
    setMentionData(mention)
    
    if (mention) {
      fetchSuggestions(mention.query)
    } else {
      setSuggestions([])
    }
  }, [onChange, parseMention, fetchSuggestions, onMentionRemove])

  // Handle mention selection
  const selectMention = useCallback((suggestion: TargetUsernameAutocompleteResult) => {
    if (!mentionData || !textareaRef.current) return

    const textarea = textareaRef.current
    const beforeMention = value.substring(0, mentionData.position)
    const afterMention = value.substring(textarea.selectionStart || 0)
    
    // Create the mention tag
    const mentionTag = `@${suggestion.target_username}`
    const newValue = beforeMention + mentionTag + ' ' + afterMention
    
    onChange(newValue)
    setMentionData(null)
    setSuggestions([])
    
    // Track the selected mention and notify parent
    selectedMentionsRef.current.set(suggestion.target_username, suggestion)
    onMentionAdd?.(suggestion)
    
    // Set cursor position after the mention
    setTimeout(() => {
      const newCursorPosition = beforeMention.length + mentionTag.length + 1
      textarea.setSelectionRange(newCursorPosition, newCursorPosition)
      textarea.focus()
    }, 0)
  }, [mentionData, value, onChange, onMentionAdd])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!mentionData || suggestions.length === 0) {
      onKeyPress?.(e)
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (suggestions[selectedIndex]) {
          selectMention(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setMentionData(null)
        setSuggestions([])
        break
      case 'Tab':
        if (suggestions[selectedIndex]) {
          e.preventDefault()
          selectMention(suggestions[selectedIndex])
        }
        break
      default:
        onKeyPress?.(e)
    }
  }, [mentionData, suggestions, selectedIndex, selectMention, onKeyPress])

  // Calculate dropdown position
  const getDropdownPosition = useCallback(() => {
    if (!mentionData || !textareaRef.current) return { top: 0, left: 0 }

    const textarea = textareaRef.current
    const { position } = mentionData

    // Simple approximation - in a real implementation you'd want more accurate positioning
    const lineHeight = 24
    const charWidth = 8
    const lines = value.substring(0, position).split('\n').length
    const lastLinePos = value.substring(0, position).split('\n').pop()?.length || 0

    const top = lines * lineHeight + 8 // Small offset
    const left = Math.min(lastLinePos * charWidth + 10, 250) // Cap at 250px from left

    return { top, left }
  }, [mentionData, value])

  const dropdownPosition = getDropdownPosition()

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => handleTextChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={cn(
          "w-full resize-none border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
      />
      
      {/* Suggestions dropdown - show if we have mentionData and either suggestions or loading */}
      {mentionData && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] w-64 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left
          }}
        >
          {isLoading && suggestions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-3 h-3 animate-spin rounded-full border border-current border-t-transparent" />
                <span>Loading...</span>
              </div>
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                className={cn(
                  "px-3 py-2 cursor-pointer text-sm border-b border-border last:border-b-0 transition-colors",
                  index === selectedIndex 
                    ? "bg-accent text-accent-foreground" 
                    : "hover:bg-accent/50 text-foreground"
                )}
                onClick={() => selectMention(suggestion)}
              >
                <div className="font-medium">@{suggestion.target_username}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
} 